import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';
import { Bar, BarChart, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { BAR_FILLS } from 'src/features/home/types';
import { useGetGatewayStatistic } from 'src/features/statistic/api/useGetGatewayStatistic';
import StatisticTooltip from 'src/features/statistic/components/StatisticTooltip';
import { DataKey, TransformedBarItem } from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import StatisticValue from 'src/registry/components/statistic/StatisticValue';
import { Button } from 'src/registry/ui/Button';
import SingleDatePicker from 'src/registry/ui/SingleDatePicker';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider.tsx';
import { useListZones } from 'src/features/zone/api/useListZones';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'src/registry/ui/DropdownMenu';
import { tableMessages } from 'src/registry/components/table';
import { AdministrativeTranslation } from 'src/features/zone/utils/types';

type CustomShapeProps = {
    dataKeys: DataKey[];
    payload?: TransformedBarItem;
    dataKey: string;
};

type SelectedZone = {
    name: string;
    id: string;
};

const CustomShape = (props: CustomShapeProps) => {
    const { payload, dataKey, dataKeys } = props;
    const lastKey = dataKeys.findLast(
        ({ value }) => payload && Object.keys(payload).includes(value) && !!payload[value],
    )?.value;

    return <Rectangle {...props} radius={lastKey === dataKey ? [0, 6, 6, 0] : undefined} />;
};

export default function VerticalBarChart({ date }: { date: dayjs.Dayjs }) {
    const { formatMessage, locale } = useIntl();
    const [zone, setZone] = React.useState<SelectedZone | null>(null);

    const [dateTime, setDateTime] = React.useState({
        date,
        time: date.format('HH:mm'),
    });

    const { parkingId } = useParking();
    const { data, isLoading } = useGetGatewayStatistic({
        from_datetime: dateTime.date.format('YYYY-MM-DD'),
        zone: zone?.id ?? null,
        parkingId,
    });

    const { data: zoneData } = useListZones({ parkingId });

    const translate = React.useCallback(
        (translations?: AdministrativeTranslation[]) => {
            if (!translations) return '-';
            const localeTranslation = translations.find((translation) => translation.language === locale)?.name;
            return localeTranslation ?? '-';
        },
        [locale],
    );

    const { newData, dataKeys } = React.useMemo((): {
        dataKeys: DataKey[];
        newData: TransformedBarItem[];
    } => {
        if (!data)
            return {
                dataKeys: [],
                newData: [],
            };

        const dataKeys: DataKey[] = Array.from(
            Object.values(data.bars_section_stats).reduce((acc, currentValue) => {
                Object.keys(currentValue).map((key) => acc.add(key));

                return acc;
            }, new Set<string>()),
        ).map((key) => ({
            title: formatMessage({ id: key.replaceAll('_', '-'), defaultMessage: key }),
            value: key,
        }));

        const newData: TransformedBarItem[] = Object.entries(data.bars_section_stats).map(([key, item]) => ({
            ...item,
            title: formatMessage({ id: key.replaceAll('_', '-') }),
        }));

        return {
            dataKeys,
            newData,
        };
    }, [data, formatMessage]);

    return (
        <GeneralBlock className="flex-1 gap-8">
            <div className="flex gap-2">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="text-sm">
                            {zone?.name ?? formatMessage(statisticMessages.allZones)}
                            <FontAwesomeIcon icon={ICON_MAP.DOWN} />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                        <DropdownMenuLabel>{formatMessage(statisticMessages.chooseZone)}</DropdownMenuLabel>
                        {!zone && <DropdownMenuSeparator />}
                        {zoneData?.results.map((z) => (
                            <DropdownMenuCheckboxItem
                                checked={z.id === zone?.id}
                                onSelect={() => {
                                    setZone({
                                        id: z.id,
                                        name: translate(z.translations),
                                    });
                                }}
                            >
                                {translate(z.translations)}
                            </DropdownMenuCheckboxItem>
                        ))}
                        <DropdownMenuSeparator />
                        <DropdownMenuItemWithIcon
                            variant="destructive"
                            icon={ICON_MAP.DELETE}
                            onSelect={() => setZone(null)}
                        >
                            {formatMessage(tableMessages.clearFilter)}
                        </DropdownMenuItemWithIcon>
                    </DropdownMenuContent>
                </DropdownMenu>
                <SingleDatePicker
                    value={new Date(dateTime.date.toISOString())}
                    buttonValue={date.format('DD.MM.YYYY')}
                    onChange={(date) => setDateTime({ ...dateTime, date: dayjs(date) })}
                />
            </div>
            <div className="flex flex-col items-center justify-center">
                <div className="flex gap-8">
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.totalPasses)}
                        value={data?.metrics_section_stats.total_passes_count}
                    />
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.failedPasses)}
                        value={data?.metrics_section_stats.failed_passes_count}
                    />
                </div>
                <div className="flex gap-8">
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.detections)}
                        value={data?.metrics_section_stats.detections_count}
                    />
                    <StatisticValue
                        isLoading={isLoading}
                        title={formatMessage(statisticMessages.vehiclesPerMinute)}
                        value={data?.metrics_section_stats.vehicles_per_minute_count}
                    />
                </div>
            </div>
            <ResponsiveContainer>
                <BarChart data={newData} layout="vertical">
                    <XAxis type="auto" hide />
                    <YAxis
                        type="category"
                        dataKey="title"
                        tickCount={4}
                        tickMargin={30}
                        interval={4}
                        tick={({ x, y, payload }) => (
                            <text x={x} y={y} dy={4} textAnchor="middle" transform={`rotate(90, ${x}, ${y})`}>
                                {payload.value}
                            </text>
                        )}
                    />
                    <Tooltip content={(props) => <StatisticTooltip {...props} />} />
                    {dataKeys.map((key, index) => (
                        <Bar
                            stackId="statistic"
                            key={index}
                            dataKey={key.value}
                            background
                            fill={BAR_FILLS[index % BAR_FILLS.length]}
                            shape={(props) => <CustomShape dataKeys={dataKeys} dataKey={key.value} {...props} />}
                        />
                    ))}
                </BarChart>
            </ResponsiveContainer>
        </GeneralBlock>
    );
}
